import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input, Label } from "./ui/input";
import { Progress } from "./ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CheckCircle2, SkipForward, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

export function GuidedReplacement({ onClose, onChanged }) {
  const [plan, setPlan] = useState(null);
  const [idx, setIdx] = useState(0);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback((keepIdx = 0) => {
    api.get("/baseline-seed/replacement-plan").then((r) => {
      setPlan(r.data);
      setIdx(Math.min(keepIdx, Math.max(r.data.steps.length - 1, 0)));
      setVals({});
    }).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(0); }, [load]);

  if (!plan) return null;
  const { steps, progress } = plan;
  const step = steps[idx];
  const pct = progress.total ? Math.round((progress.replaced / progress.total) * 100) : 100;

  const after = () => { onChanged(); load(idx); };

  const saveSeries = async () => {
    setBusy(true);
    try {
      const values = step.records.map((r) => ({ id: r.id, value: vals[r.id] ?? r.value }));
      const res = await api.post("/baseline-seed/replace-series", { collection: step.collection, values });
      toast.success(`"${step.label}" saved as actual (${res.data.updated} record(s))`);
      after();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const saveRecord = async () => {
    setBusy(true);
    try {
      const fields = Object.fromEntries((step.fields || []).map((f) => [f.key, vals[f.key] ?? f.value]));
      await api.post("/baseline-seed/replace-record", { collection: step.collection, id: step.id, fields });
      toast.success(`"${step.label}" reviewed and declared actual`);
      after();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      const ids = step.type === "series" ? step.records.map((r) => r.id) : [step.id];
      await api.post("/baseline-seed/remove", { collection: step.collection, ids });
      toast.success(`"${step.label}" removed — it didn't apply to you`);
      after();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="guided-replacement-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-accent" /> Guided replacement
          </DialogTitle>
        </DialogHeader>
        <div>
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span data-testid="gr-progress-label">{progress.replaced} of {progress.total} metrics replaced</span>
            <span>{steps.length} remaining</span>
          </div>
          <Progress value={pct} className="h-1.5" data-testid="gr-progress-bar" />
        </div>

        {!step && (
          <div className="text-sm py-6 text-center" data-testid="gr-done">
            <CheckCircle2 className="h-8 w-8 text-[#00E65B] mx-auto mb-2" />
            All synthetic metrics have been replaced or removed — your data is fully yours now.
          </div>
        )}

        {step && (
          <div className="space-y-3" data-testid={`gr-step-${idx}`}>
            <div>
              <div className="font-medium text-sm flex items-center gap-2">
                <Badge variant="warning" className="text-[9px]">Synthetic</Badge>{step.label}
              </div>
              <div className="text-[11px] text-muted-foreground">{step.sub}{step.unit ? ` · ${step.unit}` : ""}</div>
            </div>

            {step.type === "series" && (
              <div className="max-h-64 overflow-y-auto border border-border rounded-sm">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-[10px] uppercase font-mono text-muted-foreground">
                    <th className="px-2 py-1.5">Period</th><th className="px-2 py-1.5">Example value</th>
                    <th className="px-2 py-1.5">Your real value ({step.unit})</th></tr></thead>
                  <tbody>
                    {step.records.map((r) => (
                      <tr key={r.id} className="border-t border-border">
                        <td className="px-2 py-1 font-mono">{r.period}</td>
                        <td className="px-2 py-1 font-mono text-muted-foreground">{r.value}</td>
                        <td className="px-2 py-1">
                          <Input type="number" step="any" className="h-7 text-xs"
                            value={vals[r.id] ?? r.value}
                            onChange={(e) => setVals((p) => ({ ...p, [r.id]: e.target.value }))}
                            data-testid={`gr-input-${r.period}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {step.type === "record" && (step.fields || []).length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {step.fields.map((f) => (
                  <div key={f.key} className={f.key === "title" || f.key === "name" || f.key === "initiative_name" || f.key === "requirement" ? "col-span-2" : ""}>
                    <Label className="text-[10px] capitalize">{f.key.replace(/_/g, " ")}</Label>
                    <Input className="h-8 text-xs" value={vals[f.key] ?? f.value ?? ""}
                      onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
                      data-testid={`gr-field-${f.key}`} />
                  </div>
                ))}
              </div>
            )}
            {step.type === "record" && (step.fields || []).length === 0 && (
              <p className="text-xs text-muted-foreground">Review this item — declaring keeps it as part of your actual profile.</p>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button variant="primary" size="sm" disabled={busy}
                onClick={step.type === "series" ? saveSeries : saveRecord} data-testid="gr-save-btn">
                <CheckCircle2 className="h-3.5 w-3.5" /> Save as actual
              </Button>
              <Button variant="outline" size="sm" disabled={busy || steps.length < 2}
                onClick={() => setIdx((i) => (i + 1) % steps.length)} data-testid="gr-skip-btn">
                <SkipForward className="h-3.5 w-3.5" /> Skip
              </Button>
              <Button variant="ghost" size="sm" className="text-red-400 ml-auto" disabled={busy}
                onClick={remove} data-testid="gr-remove-btn">
                <Trash2 className="h-3.5 w-3.5" /> Remove — doesn't apply
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
